import { NOTFALL_FELDER, notfallStellen, notfallWert } from '../bestandUebernahme';
import type { NotrufplanInhalt } from '../../sige-plan/types';

/**
 * Was der Plan aus `asplaene.notfall` ausweist — und was er als fehlend
 * benennt.
 *
 * ═══ WOZU ES DIESE DATEI GIBT ═════════════════════════════════════════════
 * Die Notfallfelder waren lange tote Spalten: von den Aushaengen gelesen, von
 * niemandem geschrieben. Seit es einen Editor dafuer gibt, stehen dort echte
 * Angaben — Sammelplatz, Rettungszufahrt, Giftnotruf, Erreichbarkeiten. Der
 * PLAN nannte sie weiterhin nicht. Ein Aushang, der den Sammelplatz benennt,
 * und ein Planblatt, das dazu schweigt, widersprechen einander; im Ernstfall
 * gilt dann das, was gerade greifbar ist.
 *
 * ═══ WARUM DIE REGEL HIER STEHT UND NICHT IM GENERATOR ════════════════════
 * Dieselbe Lehre wie bei der Anlagenliste: der Generator druckt, er
 * entscheidet nicht. Welches Feld in welchen Abschnitt gehoert, welcher Wert
 * als vorhanden gilt und wie die Luecke heisst, steht hier und ist ohne DOM
 * geprueft. Gelesen wird ausschliesslich ueber `notfallWert` aus
 * `bestandUebernahme.ts` — eine zweite Leseregel im Generator waere eine
 * zweite Fassung derselben Wahrheit, und die zweite ist erfahrungsgemaess die
 * aeltere.
 *
 * ═══ WARUM DIE DATEI IM ORDNER `ablage` LIEGT ═════════════════════════════
 * Fachlich gehoerte sie neben `bestandUebernahme.ts`. Diese Datei traegt
 * jedoch 44 Tests eines anderen Bearbeiters und wird in dieser Sitzung nicht
 * angefasst; die Dateihoheit reicht ueber `ablage/**` und den Generator.
 * Verschieben laesst sich das jederzeit — die Aussagen aendert es nicht.
 *
 * ═══ KEIN ERSATZWERT ══════════════════════════════════════════════════════
 * Ein leeres Feld wird BENANNT, nicht mit einem Gedankenstrich gefuellt.
 * Genau diese Ueberlegung steht schon ueber `notrufLuecken`: ein „—" sieht
 * aus wie eine geprueft-leere Angabe, ein roter Satz nicht.
 */

/** Die drei Abschnitte des Plans, in denen Notfallangaben stehen. */
export type NotfallBlock = 'notruf' | 'rettung' | 'brandschutz';

/** Ueberschrift des Abschnitts im gedruckten Plan. */
export const NOTFALL_BLOCK_TITEL: Record<NotfallBlock, string> = {
    notruf: 'Erreichbarkeiten im Notfall',
    rettung: 'Sammelplatz, Rettungszufahrt, Ersthelfer',
    brandschutz: 'Löschmittel und Standorte',
};

/** Wie der offene Punkt eingeleitet wird. */
const NOTFALL_BLOCK_LUECKE: Record<NotfallBlock, string> = {
    notruf: 'OFFEN: Diese Angaben zur Notfallorganisation fehlen',
    rettung: 'OFFEN: Diese Angaben zu Erster Hilfe und Rettung fehlen',
    brandschutz: 'OFFEN: Diese Angaben zum Brandschutz fehlen',
};

/**
 * Welches Katalogfeld in welchen Abschnitt gehoert.
 *
 * Ein Feld, das der Katalog spaeter ergaenzt und das hier fehlt, faellt NICHT
 * aus dem Plan: es landet im Abschnitt `notruf`. Ein Test haelt fest, dass
 * jeder Eintrag aus `NOTFALL_FELDER` gedruckt wird — sonst waere die stille
 * Auslassung genau der Fehler, gegen den dieses Modul sonst ueberall
 * anschreibt.
 */
const BLOCK_JE_FELD: Record<string, NotfallBlock> = {
    giftnotruf: 'notruf',
    koordinator_tel: 'notruf',
    bauleitung_tel: 'notruf',
    sammelplatz: 'rettung',
    zufahrt_breite: 'rettung',
    ersthelfer_quote: 'rettung',
    loescher_standorte: 'brandschutz',
};

const VORGABE_BLOCK: NotfallBlock = 'notruf';

/** Der Abschnitt, in dem ein Feld gedruckt wird. */
export const blockDesFeldes = (key: string): NotfallBlock =>
    BLOCK_JE_FELD[key] || VORGABE_BLOCK;

export interface NotfallAngabe {
    key: string;
    label: string;
    /** Der eingetragene Text; leer, wenn nichts hinterlegt ist. */
    wert: string;
    vorhanden: boolean;
    block: NotfallBlock;
}

/**
 * Die Angaben eines Abschnitts, in der Reihenfolge des Katalogs.
 *
 * Die Reihenfolge ist nicht beliebig: `NOTFALL_FELDER` fuehrt sie so, wie sie
 * im Editor stehen, und ein Plan, der sie anders sortiert, laesst sich nicht
 * gegen die Eingabemaske gegenlesen.
 */
export const notfallAngaben = (
    notfall: Record<string, unknown> | null | undefined,
    block: NotfallBlock,
): NotfallAngabe[] =>
    NOTFALL_FELDER
        .filter(f => blockDesFeldes(f.key) === block)
        .map(f => {
            const wert = notfallWert(notfall, f.key);
            return {
                key: f.key,
                label: f.label,
                wert,
                vorhanden: wert.length > 0,
                block,
            };
        });

/** Die Zeilen fuer die Tabelle — nur, was tatsaechlich hinterlegt ist. */
export const notfallZeilen = (angaben: readonly NotfallAngabe[]): string[][] =>
    (angaben || []).filter(a => a.vorhanden).map(a => [a.label, a.wert]);

/**
 * Der rote Satz unter der Tabelle, oder `null`, wenn nichts fehlt.
 *
 * Die fehlenden Angaben werden NAMENTLICH genannt und nicht gezaehlt: „zwei
 * Angaben fehlen" zwingt zum Nachsehen, „Sammelplatz fehlt" nicht.
 */
export const notfallLuecke = (angaben: readonly NotfallAngabe[]): string | null => {
    const offen = (angaben || []).filter(a => !a.vorhanden);
    if (offen.length === 0) return null;
    const block = offen[0].block;
    return `${NOTFALL_BLOCK_LUECKE[block]}: ${offen.map(a => a.label).join('; ')}.`;
};

// ═══════════════════════════════════════════════════════════════════════════
// DIE RETTUNGSKETTE — AUS DEM NOTRUFPLAN DES SIGE-PLANS GELESEN
// ═══════════════════════════════════════════════════════════════════════════
//
// ═══ WARUM DAS UEBERHAUPT IN DEN PLAN GEHOERT ══════════════════════════════
// Anhang 3 verlangt die Rettungskette namentlich. Der Aushang „Rettungskette"
// nennt Klinik und Durchgangsarzt seit jeher; das PLANBLATT schwieg dazu. Ein
// Plan, dessen Aushang die Klinik benennt, und ein Planblatt, das sie nicht
// kennt, widersprechen einander — derselbe Widerspruch, der fuer den
// Sammelplatz oben schon aufgeloest ist.
//
// ═══ KEINE KOPIE, KEIN ZWEITER LADEWEG ═════════════════════════════════════
// Der Notrufplan gehoert dem SiGe-Plan (Plan Abschnitt 10: „lesen — EINE
// Quelle"). Er wandert NICHT in die `asplan_`-Tabellen und wird auch nicht im
// Generator nachgeladen: der Container liest ihn einmal und reicht ihn als
// Option durch — genau wie die Schleusenparameter.
//
// ═══ WAS ALS STELLE ZAEHLT, ENTSCHEIDET HIER NIEMAND NEU ═══════════════════
// Die Regel steht in `bestandUebernahme.ts:notfallStellen` und ist dort
// geprueft (eine Stelle ohne Namen UND ohne Nummer ist ein angefangenes
// Formular, kein Eintrag). Diese Datei projiziert nur auf Tabellenzeilen.
// Damit sagen Aushang und Planblatt dasselbe — und zwar deshalb, weil sie
// dieselbe Funktion fragen.

/**
 * Der Notrufplan, wie ihn der Generator bekommt.
 *
 * Die drei Zustaende sind ausdruecklich verschieden:
 *  • `undefined` — nicht mitgegeben. Ueber den Notrufplan ist damit NICHTS
 *    bekannt, und der Plan darf nicht behaupten, es sei keiner gepflegt.
 *  • `null` — gelesen, es gibt keinen.
 *  • Inhalt — gelesen.
 */
export type NotrufplanEingabe = NotrufplanInhalt | null | undefined;

/** Die Spalten der Rettungskettentabelle — dieselben wie auf dem Aushang. */
export const RETTUNGSKETTE_KOPF = [
    'Stelle', 'Name', 'Anschrift', 'Telefon', 'Entfernung', 'Stand',
];

const LUECKE_OHNE_QUELLE =
    'OFFEN: Die Rettungskette ist in diesem Ausdruck nicht ausgewiesen, denn der '
    + 'Notrufplan des SiGe-Plans lag beim Erstellen nicht vor. Bis dahin gilt '
    + 'allein die 112.';

const LUECKE_OHNE_STELLE =
    'OFFEN: Im Notrufplan des SiGe-Plans ist keine Notfallstelle hinterlegt '
    + '(Anhang 3: Rettungskette). Bis dahin gilt allein die 112; Klinik und '
    + 'Durchgangsarzt werden im SiGe-Plan festgelegt.';

export interface RettungsketteBlock {
    /** Tabellenzeilen zu `RETTUNGSKETTE_KOPF`; leer, wenn nichts vorliegt. */
    zeilen: string[][];
    /** Der rote Satz, oder `null`, wenn die Kette steht. */
    luecke: string | null;
}

/**
 * Die Rettungskette fuer das Planblatt.
 *
 * Wie auf dem Aushang: eine fehlende Pflichtangabe wird BENANNT („ohne
 * Namen", „nicht hinterlegt") und nie durch einen Gedankenstrich ersetzt. Die
 * Entfernung bleibt leer, wenn sie nicht ermittelt wurde — sie ist ein
 * abgeleiteter Komfortwert und keine Festlegung, und der Aushang haelt es
 * genauso.
 *
 * Die Marke „nur gefunden" wird MITGEDRUCKT statt weggelassen: wer die
 * Rettungskette plant, muss sehen, welche Zeile ein Mensch angerufen hat.
 */
export const rettungskette = (inhalt: NotrufplanEingabe): RettungsketteBlock => {
    if (inhalt === undefined) return { zeilen: [], luecke: LUECKE_OHNE_QUELLE };

    const stellen = notfallStellen(inhalt);
    if (stellen.length === 0) return { zeilen: [], luecke: LUECKE_OHNE_STELLE };

    return {
        zeilen: stellen.map(s => [
            s.was,
            [s.name || 'ohne Namen', s.hinweis].filter(Boolean).join('\n'),
            s.adresse,
            s.telefon || 'nicht hinterlegt',
            s.entfernung,
            s.geprueft ? 'bestätigt' : 'nur gefunden',
        ]),
        luecke: null,
    };
};
